/**
 * Battle flashlight palette (Profile + BattleUI + WS sync). Keys are lowercase.
 */

export const FLASHLIGHT_COLOR_KEYS = [
  'red',
  'green',
  'blue',
  'yellow',
  'white',
  'purple',
  'pink',
  'orange',
];

const PALETTE = {
  white: ['rgba(255, 250, 230, 0.38)', 'rgba(255, 245, 210, 0.16)', 'rgba(255, 240, 200, 0.05)'],
  red: ['rgba(255, 120, 120, 0.42)', 'rgba(255, 60, 60, 0.2)', 'rgba(220, 40, 55, 0.06)'],
  green: ['rgba(120, 255, 160, 0.4)', 'rgba(60, 220, 120, 0.18)', 'rgba(40, 180, 90, 0.06)'],
  blue: ['rgba(140, 200, 255, 0.42)', 'rgba(80, 150, 255, 0.2)', 'rgba(50, 100, 220, 0.06)'],
  yellow: ['rgba(255, 250, 120, 0.42)', 'rgba(255, 230, 80, 0.2)', 'rgba(220, 190, 40, 0.06)'],
  purple: ['rgba(200, 140, 255, 0.42)', 'rgba(160, 80, 220, 0.2)', 'rgba(120, 50, 180, 0.06)'],
  pink: ['rgba(255, 160, 210, 0.42)', 'rgba(255, 100, 180, 0.2)', 'rgba(220, 60, 140, 0.06)'],
  orange: ['rgba(255, 190, 120, 0.42)', 'rgba(255, 140, 60, 0.2)', 'rgba(220, 100, 40, 0.06)'],
};

export function isValidFlashlightColorKey(value) {
  if (value == null || typeof value !== 'string') return false;
  return FLASHLIGHT_COLOR_KEYS.includes(value.toLowerCase());
}

/** For server / saved profile: throws if invalid */
export function assertValidFlashlightColorKey(value) {
  if (!isValidFlashlightColorKey(value)) {
    throw new Error(`Invalid flashlight color: ${value}`);
  }
  return value.toLowerCase();
}

/** Opponent or legacy payloads: safe key for rendering */
export function normalizeFlashlightColorForDisplay(value) {
  if (value == null || typeof value !== 'string') return 'white';
  const k = value.toLowerCase();
  return FLASHLIGHT_COLOR_KEYS.includes(k) ? k : 'white';
}

export function buildFlashlightRadialGradientBackground(colorKey) {
  const k = normalizeFlashlightColorForDisplay(colorKey);
  const [a, b, c] = PALETTE[k];
  return `radial-gradient(circle 105px at var(--flashlight-x, 50%) var(--flashlight-y, 50%), ${a} 0%, ${b} 45%, ${c} 72%, transparent 100%)`;
}

/** Scoped overlays (e.g. minigame play arena) where beam position is percent of a container. */
export function buildFlashlightRadialGradientBackgroundAtPercent(colorKey, xPct, yPct) {
  const k = normalizeFlashlightColorForDisplay(colorKey);
  const [a, b, c] = PALETTE[k];
  return `radial-gradient(circle 105px at ${xPct}% ${yPct}%, ${a} 0%, ${b} 45%, ${c} 72%, transparent 100%)`;
}

/** Small centered preview (e.g. Profile grid) — no pointer CSS vars */
export function buildFlashlightSwatchBackground(colorKey) {
  const k = normalizeFlashlightColorForDisplay(colorKey);
  const [a, b, c] = PALETTE[k];
  return `radial-gradient(circle 32px at 50% 50%, ${a} 0%, ${b} 50%, ${c} 78%, transparent 100%)`;
}
